/**
 * SignPage — public, no-login page a client opens from the emailed link to review
 * and digitally sign an agreement (e.g. a retainer).
 *
 * Flow: GET /agreements/public/:requestCode -> show the exact filled PDF -> the client
 * draws + types their signature (reusing SignaturePad) -> POST /signatures/submit ->
 * the server stamps client + firm signatures into an immutable signed PDF.
 *
 * Public route (see App.js standalone block). Hits maven-platform-server directly with
 * plain axios (no Auth0) since the signer may not have an account.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import LogoLoader from '../LogoLoader';
import styled from 'styled-components';
import SignaturePad from '../SignaturePad';
import { useToast } from '../../Context/ToastContext';

const MAVEN_API_BASE = process.env.REACT_APP_MAVEN_SERVER || 'https://sea-lion-app-7wbpx.ondigitalocean.app';


const Page = styled.div`
  min-height: 100vh; width: 100%; display: flex; flex-direction: column; align-items: center;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 100%); color: #33445e;
  padding: 24px 16px; box-sizing: border-box;
`;
const Card = styled.div`
  width: 100%; max-width: 920px; background: rgba(244, 245, 249, 0.92); border: 1px solid rgba(120,140,180,0.18);
  border-radius: 16px; padding: 22px 24px; margin-top: 18px; backdrop-filter: blur(8px);
`;
const Title = styled.h1`font-size: 22px; margin: 0 0 4px; color: #192947;`;
const Muted = styled.p`margin: 0; color: #8b93a7; font-size: 14px;`;
const Frame = styled.iframe`
  width: 100%; height: 70vh; border: none; border-radius: 12px; background: #fff; margin-top: 16px;
`;
const Bar = styled.div`
  position: sticky; bottom: 0; width: 100%; max-width: 920px; display: flex; gap: 12px; justify-content: center;
  padding: 16px 0 6px;
`;
const Button = styled.button`
  padding: 14px 34px; border: none; border-radius: 12px; font-weight: 700; font-size: 16px; cursor: pointer;
  color: #fff; background: linear-gradient(110deg, #2d5bed, #5b61ea);
  box-shadow: 0 7px 18px rgba(70, 92, 213, 0.19); opacity: ${p => p.disabled ? 0.6 : 1};
`;
const Center = styled.div`min-height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: linear-gradient(135deg,#f9fbff,#ffffff); color: #33445e;`;
const Badge = styled.div`
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; font-weight: 700;
  color: #0f8f63; background: rgba(18, 165, 113, 0.14); border: 1px solid rgba(18, 165, 113, 0.35); font-size: 15px;
`;

export default function SignPage() {
  const { requestCode } = useParams();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${MAVEN_API_BASE}/agreements/public/${requestCode}`);
      setData(r.data.data);
      if (r.data.data.signed || r.data.data.status === 'signed') setDone(true);
    } catch (e) {
      setError(e.response?.data?.message || 'This signing link is invalid or has expired.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [requestCode]);

  const handleSubmit = async (signatureData) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${MAVEN_API_BASE}/signatures/submit`, {
        ...signatureData,
        userName: data?.clientName || signatureData?.typedSignature?.text || 'Client',
      });
      if (res.data?.success) {
        setPadOpen(false);
        setDone(true);
        showToast('Signed — thank you!', 'success');
      } else {
        showToast(res.data?.message || 'Could not submit signature', 'error');
      }
    } catch (e) {
      showToast(e.response?.data?.message || 'Could not submit signature', 'error');
    } finally { setSubmitting(false); }
  };

  if (loading) return <Center><LogoLoader size={170} message="Loading your document" /></Center>;
  if (error) return <Center><div style={{ fontSize: 18 }}>⚠️ {error}</div></Center>;

  if (done) {
    return (
      <Center>
        <Badge>✓ Signed</Badge>
        <div style={{ fontSize: 18, textAlign: 'center', maxWidth: 460 }}>
          Thank you, {data?.clientName || 'there'}. Your signature has been recorded and
          {' '}<b>{data?.title}</b> is now complete. Your adviser has been notified and keeps the signed copy.
        </div>
      </Center>
    );
  }

  return (
    <Page>
      <Card>
        <Title>{data?.title || 'Agreement'}</Title>
        <Muted>Prepared for {data?.clientName || 'you'}. Please review the full document below, then sign.</Muted>
        {data?.draftUrl && <Frame title="Agreement document" src={data.draftUrl} />}
      </Card>

      <Bar>
        <Button onClick={() => setPadOpen(true)}>Review complete — Sign</Button>
      </Bar>

      <SignaturePad
        isOpen={padOpen}
        onClose={() => setPadOpen(false)}
        onSubmit={handleSubmit}
        onDecline={() => setPadOpen(false)}
        purpose={data?.title}
        description={data?.consentText}
        requestCode={requestCode}
        isSubmitting={submitting}
      />
    </Page>
  );
}
